import { consumeAutoLoginToken } from "./authService";
import { setAuthenticatedUser } from "../auth/service";
import storeApi from "./storeApi";
import { leadMobileFromUrl } from "./leadMobileFromUrl";

function assertObject(value, label) {
  if (!value || typeof value !== "object") {
    throw new Error(`Expected ${label} to be an object`);
  }
}

function assertString(value, label) {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`Expected ${label} to be a non-empty string`);
  }
}

function requireSessionResult(res) {
  assertObject(res, "session response");
  const { token, user } = res;
  assertString(token, "session response.token");
  // user is required for UI; keep strict so we don't silently "login" without a user.
  assertObject(user, "session response.user");
  return { token: token.trim(), user };
}

/**
 * Store auto-login based on URL params:
 * - m: mobile (required)
 * - t: secret token (required)
 *
 * Uses Store endpoint (POST /api/store/auto_login), persists store token, then strips params.
 */
export async function maybeAutoLoginFromQuery({
  location,
  navigate,
  setCurrentUser,
}) {
  try {
    // If already authenticated for Store APIs, do nothing.
    const existingStoreToken = localStorage.getItem("storeAccessToken");
    if (existingStoreToken) return;

    const urlParams = new URLSearchParams(location.search || "");
    const mobile =
      leadMobileFromUrl(
        `${window.location.origin}${location.pathname}${location.search || ""}`
      ) || urlParams.get("m");
    const tokenParam = urlParams.get("t");
    if (!mobile || !tokenParam) return;

    const raw = await consumeAutoLoginToken({
      token: tokenParam,
      mobile,
    });

    const { token, user } = requireSessionResult(raw);

    // Store token for /api/store/* Authorization.
    localStorage.setItem("storeAccessToken", token);
    // Keep app auth in sync (some UI relies on currentUser/accessToken).
    setAuthenticatedUser({ token, user });
    if (typeof setCurrentUser === "function") setCurrentUser(user);

    // Verify Store session so backend associates the request with a store user.
    try {
      const sessionResponse = await storeApi.sessionAPI.getCurrentSession();
      const storeUser =
        sessionResponse?.user ||
        sessionResponse?.data?.user ||
        sessionResponse?.data?.data?.user ||
        sessionResponse?.data?.data ||
        sessionResponse?.data;
      if (storeUser && typeof setCurrentUser === "function") {
        setCurrentUser(storeUser);
      }
    } catch (sessionErr) {
      // Non-fatal: auto-login already returned a valid token/user.
      // eslint-disable-next-line no-console
      console.warn("Store session verification failed", sessionErr);
    }

    // Strip params from URL so they don't linger in share/copy.
    urlParams.delete("t");
    urlParams.delete("m");

    navigate(
      {
        pathname: location.pathname,
        search: urlParams.toString() ? `?${urlParams.toString()}` : "",
      },
      { replace: true, state: location.state }
    );
  } catch (e) {
    // Non-fatal: collections pages should still load for guest users.
    // eslint-disable-next-line no-console
    console.warn("Store auto-login failed", e);
  }
}

